import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal as AntModal, Input as AntInput, Button as AntButton, message, Tabs as AntTabs } from 'antd';
import { getUserCompanyInfo, uploadS3, saveGroupInfoAttachment, getGroupEventInfo } from '../services/apiService';
import { getCompanySmtpSettings, sendEmail } from '../services/smtpService';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import html2canvas from '../utils/html2canvasLight';
import jsPDF from 'jspdf';

const fieldStyle = 'w-full px-3 py-2 border border-gray-300 rounded-md';

const InvoiceModal = ({ open, onClose, reservationInfo, saleInfo, priceInfo, depositSummary, orderNo, saleNo, travelers = [], extraServices = [], groupNo, ordererEmail: propOrdererEmail }) => {
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [receiverCompany, setReceiverCompany] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [note, setNote] = useState('');
  const [cancelPolicy, setCancelPolicy] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [invoiceType, setInvoiceType] = useState('full');
  const [depositPercent, setDepositPercent] = useState(30);
  const [showTravelerDetails, setShowTravelerDetails] = useState(false);
  const printRef = useRef(null);

  const parseMoney = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const s = String(v).trim();
    if (!s) return 0;
    const normalized = s.replace(/,/g, '');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    return num >= 100 ? num.toLocaleString('ko-KR') : String(num);
  };
  
  // Email sending states
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState('');

  // Default email body template
  const getDefaultEmailBody = (pdfUrl, companyName, productName, eventPeriod) => {
    return `<!DOCTYPE html>
<html>
  <body style="font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif; color:#111827; line-height:1.7; font-size:14px; margin:0; padding:0;">
    <div style="max-width:640px; margin:0 auto; padding:16px;">
      <p style="margin:0 0 12px 0;">안녕하세요.</p>
      <p style="margin:0 0 20px 0;">${companyName || '저희 여행사'}입니다.<br/>요청하신 인보이스를 발송해 드립니다.</p>

      <div style="background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin:0 0 16px 0;">
        <div style="margin:0 0 6px 0;"><strong style="color:#374151;">■ 상품명:</strong> ${productName || '-'}</div>
        <div><strong style="color:#374151;">■ 행사기간:</strong> ${eventPeriod || '-'}</div>
      </div>

      <p style="margin:0 0 12px 0;">아래 버튼을 클릭하여 인보이스를 확인하실 수 있습니다.</p>
      <p style="margin:0 0 20px 0;">
        <a href="${pdfUrl || '#'}" target="_blank" style="display:inline-block; background:#2563eb; color:#ffffff; text-decoration:none; padding:10px 16px; border-radius:8px; font-weight:600;">인보이스 열기</a>
      </p>

      ${pdfUrl ? `<p style="margin:0 0 20px 0; color:#6b7280; font-size:12px;">버튼이 보이지 않으면 아래 링크를 복사하여 브라우저에 붙여넣기 해주세요.<br/><span style="word-break:break-all;">${pdfUrl}</span></p>` : ''}

      <p style="margin:0 0 12px 0;">문의사항이 있으시면 언제든지 연락 주시기 바랍니다.</p>
      <p style="margin:0 0 24px 0;">감사합니다.</p>
      <p style="margin:0;">${companyName || ''}</p>
    </div>
  </body>
</html>`;
  };

  useEffect(() => {
    if (!open) return;
(async () => {
      try {
        setLoading(true);
        const info = await getUserCompanyInfo();
        setCompany(info || null);
        const defaultReceiver = (reservationInfo?.주문자 || '') || '';
        setReceiverCompany('');
        setReceiverName(defaultReceiver);
        setBankAccount('');
        setInvoiceType('full');
        setDepositPercent(30);
        setShowTravelerDetails(false);
        
        // Load SMTP settings
        try {
          const smtp = await getCompanySmtpSettings();
          setSmtpSettings(smtp || null);
        } catch (smtpErr) {
          console.warn('SMTP 설정을 불러오지 못했습니다:', smtpErr);
        }

        // Auto fill deposit bank account from the first registered company account
        try {
          const { companyNo } = getUserInfo() || {};
          const payload = {
            Table1: [
              { 컬럼명: '@업체번호', 값: String(companyNo ?? '1') },
            ],
          };
          const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4';
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error('failed');
          const data = await res.json();
          const list = Array.isArray(data?.table8) ? data.table8 : [];
          const first = list?.[0] || null;
          if (first) {
            const parts = [
              first?.은행명 ?? '',
              first?.계좌번호 ?? '',
              first?.예금주 ? `예금주 ${first?.예금주}` : '',
            ].map((s) => String(s || '').trim()).filter(Boolean);
            const formatted = parts.join(' ');
            if (formatted) {
              setBankAccount((prev) => (String(prev || '').trim() ? prev : formatted));
            }
          }
        } catch (bankErr) {
          try { console.warn('[InvoiceModal] failed to auto fill bank account', bankErr); } catch { }
        }

        // Auto fill receiver company/name based on agency number (대리점번호)
        try {
          const agencyNoRaw = reservationInfo?.대리점번호 ?? reservationInfo?.대리점코드 ?? saleInfo?.대리점번호 ?? saleInfo?.대리점코드 ?? null;
          const agencyNo = agencyNoRaw != null ? String(agencyNoRaw).trim() : '';
          if (agencyNo) {
            const { companyNo } = getUserInfo() || {};
            const payload = {
              Table1: [
                { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
                { 컬럼명: '@사용여부1', 값: false },
                { 컬럼명: '@사용여부2', 값: true },
              ],
            };
            const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98';
            const res = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
              body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('failed');
            const data = await res.json();
            const list = Array.isArray(data?.table) ? data.table : [];
            const matched = list.find((row) => String(row?.업체번호2 ?? row?.거래처번호 ?? '').trim() === agencyNo) || null;
            if (matched) {
              const nextCompany = String(matched?.거래처명 || '').trim();
              const nextName = String(matched?.대표자명 || '').trim() || String(matched?.담당자명 || '').trim() || defaultReceiver;
              if (nextCompany) {
                setReceiverCompany((prev) => (String(prev || '').trim() ? prev : nextCompany));
              }
              if (nextName) {
                setReceiverName((prev) => (String(prev || '').trim() ? prev : nextName));
              }
            }
          }
        } catch (receiverErr) {
          try { console.warn('[InvoiceModal] failed to auto fill receiver info', receiverErr); } catch { }
        }
      } catch (e) {
        message.error(e?.message || '회사 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, reservationInfo, saleInfo, priceInfo, depositSummary]);

  const totalAmount = useMemo(() => {
    const p = priceInfo || {};
    const sum = (parseMoney(p?.상품금액원화) || 0) + (parseMoney(p?.택스및유류원화) || 0) - (parseMoney(p?.취소료원화) || 0);
    const finalPay = parseMoney(p?.고객결제가원화) || sum;
    return finalPay;
  }, [priceInfo]);

  const saleBreakdown = useMemo(() => {
    const p = priceInfo || {};
    const 상품금액 = parseMoney(p?.상품금액원화) || 0;
    const 텍스유류 = parseMoney(p?.택스및유류원화) || 0;
    const 취소료 = parseMoney(p?.취소료원화) || 0;
    const 프로모션할인 = parseMoney(p?.프로모션할인원화 ?? p?.프로모션할인 ?? p?.할인금액원화 ?? 0) || 0;
    const 기타할인 = parseMoney(p?.기타할인원화 ?? p?.기타할인 ?? p?.기타할인금액원화 ?? p?.기타할인금액 ?? 0) || 0;
    const 대리점커미션 = parseMoney(p?.대리점커미션원화 ?? p?.대리점커미션 ?? p?.커미션원화 ?? 0) || 0;
    const 추가서비스합계 = (Array.isArray(extraServices) ? extraServices : []).reduce((sum, r) => sum + (parseMoney(r?.판매금액) || 0), 0);
    const computedNet = 상품금액 + 추가서비스합계 + 텍스유류 - 프로모션할인 - 기타할인 - 대리점커미션 - 취소료;
    const netFromField = parseMoney(p?.넷가원화) || 0;
    const net = netFromField > 0 ? netFromField : computedNet;
    return {
      상품금액,
      추가서비스합계,
      텍스유류,
      프로모션할인,
      기타할인,
      대리점커미션,
      취소료,
      computedNet,
      netFromField,
      net,
    };
  }, [priceInfo, extraServices]);

  const effectiveSaleTotal = useMemo(() => {
    const v = parseMoney(saleBreakdown?.net);
    if (Number.isFinite(v) && v > 0) return v;
    const fallback = parseMoney(totalAmount) || 0;
    return fallback;
  }, [saleBreakdown, totalAmount]);

  const paymentSummary = useMemo(() => {
    const total = parseMoney(effectiveSaleTotal) || 0;
    const paidRaw = depositSummary?.입금액 ?? depositSummary?.입금 ?? depositSummary?.입금금액;
    const dueRaw = depositSummary?.미수금 ?? depositSummary?.미수 ?? depositSummary?.잔액;
    const paid = parseMoney(paidRaw);
    const hasDue = dueRaw !== null && dueRaw !== undefined && String(dueRaw).trim() !== '';
    const dueParsed = parseMoney(dueRaw);
    const computedDue = Math.max(0, (Number(total) || 0) - paid);
    const dueCandidate = hasDue ? dueParsed : computedDue;
    const due = (dueCandidate > total || dueCandidate < 0) ? computedDue : dueCandidate;
    return {
      total: Number(total) || 0,
      paid,
      due,
    };
  }, [depositSummary, effectiveSaleTotal]);

  const depositInputNumber = useMemo(() => {
    const n = Number(depositPercent);
    return Number.isFinite(n) ? n : 0;
  }, [depositPercent]);

  const isDepositFixedAmount = useMemo(() => {
    return depositInputNumber > 100;
  }, [depositInputNumber]);

  const normalizedDepositPercent = useMemo(() => {
    if (isDepositFixedAmount) return 0;
    return Math.max(0, Math.min(100, depositInputNumber));
  }, [depositInputNumber, isDepositFixedAmount]);

  const invoiceRequestAmount = useMemo(() => {
    const total = parseMoney(effectiveSaleTotal) || 0;
    if (invoiceType === 'deposit') {
      if (isDepositFixedAmount) {
        const v = Math.round(depositInputNumber);
        return Math.max(0, Math.min(total, v));
      }
      const v = Math.round(total * (normalizedDepositPercent / 100));
      return Math.max(0, Math.min(total, v));
    }
    if (invoiceType === 'balance') {
      const due = parseMoney(paymentSummary?.due) || 0;
      return Math.max(0, Math.min(total, due));
    }
    return total;
  }, [effectiveSaleTotal, invoiceType, isDepositFixedAmount, depositInputNumber, normalizedDepositPercent, paymentSummary]);

  const prePaidAmount = useMemo(() => {
    const paid = parseMoney(paymentSummary?.paid) || 0;
    return Math.max(0, paid);
  }, [paymentSummary]);

  const validateInvoiceOptions = () => {
    if (invoiceType === 'deposit') {
      if (!isDepositFixedAmount) {
        if (!(normalizedDepositPercent > 0 && normalizedDepositPercent <= 100)) {
          message.error('신청금 비율(%)을 1~100 사이로 입력해주세요.');
          return false;
        }
      }
      if (!(invoiceRequestAmount > 0)) {
        message.error('신청금 청구 금액이 0원입니다.');
        return false;
      }
    }
    if (invoiceType === 'balance') {
      if (!(invoiceRequestAmount > 0)) {
        message.error('미수금이 없어 잔금 인보이스를 생성할 수 없습니다.');
        return false;
      }
    }
    return true;
  };

  const travelerSummary = useMemo(() => {
    const total = Array.isArray(travelers) ? travelers.length : 0;
    const byGroup = {};
    (Array.isArray(travelers) ? travelers : []).forEach(t => {
      const g = (t?.연령구분 ?? '').toString() || '기타';
      byGroup[g] = (byGroup[g] || 0) + 1;
    });
    return { total, byGroup };
  }, [travelers]);

  const travelerRows = useMemo(() => {
    return (Array.isArray(travelers) ? travelers : []).map((t, idx) => {
      const name = t?.한글이름 || t?.여행객명 || '';
      const group = t?.연령구분 || '';
      const price = Number(t?.상품금액원화) || 0;
      const tax = Number(t?.택스및유류원화) || 0;
      const total = price + tax;
      return { idx: idx + 1, name, group, price, tax, total };
    });
  }, [travelers]);

  const travelerTotals = useMemo(() => {
    return travelerRows.reduce((acc, r) => {
      acc.price += r.price;
      acc.tax += r.tax;
      acc.total += r.total;
      return acc;
    }, { price: 0, tax: 0, total: 0 });
  }, [travelerRows]);

  const eventPeriod = useMemo(() => {
    const si = saleInfo || {};
    const ri = reservationInfo || {};
    const prefer = si?.행사기간 || ri?.행사기간;
    if (prefer) return prefer;
    const startRaw = si?.행사시작일 || si?.시작일 || ri?.행사시작일 || ri?.시작일 || '';
    const endRaw = si?.행사종료일 || si?.행사종료일시 || si?.종료일 || ri?.행사종료일 || ri?.행사종료일시 || ri?.종료일 || '';
    const toDateOnly = (v) => {
      const s = String(v || '').trim();
      if (!s) return '';
      if (s.includes(' ')) return s.split(' ')[0];
      if (s.includes('T')) return s.split('T')[0];
      const m = s.match(/^\d{4}[./-]\d{1,2}[./-]\d{1,2}/);
      return m ? m[0] : s;
    };
    const s = toDateOnly(startRaw);
    const e = toDateOnly(endRaw);
    return s && e ? `${s} ~ ${e}` : '';
  }, [saleInfo, reservationInfo]);

  const onPrint = () => {
    try {
      if (!validateInvoiceOptions()) return;
      const node = printRef.current;
      if (!node) return;
      const printWindow = window.open('', '_blank', 'width=900,height=800');
      if (!printWindow) return;
      const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Invoice</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Apple SD Gothic Neo', 'Malgun Gothic', Arial, sans-serif; color: #111827; background: #ffffff; }
  .wrap { max-width: 900px; margin: 0 auto; padding: 40px 32px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
  .header-left { flex: 1; }
  .header-right { text-align: right; }
  .invoice-title { font-size: 32px; font-weight: 700; color: #1f2937; letter-spacing: -0.5px; margin-bottom: 8px; }
  .order-no { font-size: 13px; color: #6b7280; font-weight: 500; }
  .company-name { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 8px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
  .section { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
  .section-title { font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
  .label { font-size: 12px; color: #6b7280; font-weight: 500; margin-bottom: 4px; }
  .value { font-size: 14px; font-weight: 600; color: #111827; line-height: 1.5; }
  .info-box { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; }
  table { width: 100%; border-collapse: collapse; background: #ffffff; border-radius: 8px; overflow: hidden; }
  th { background: #f3f4f6; color: #374151; font-weight: 600; font-size: 12px; padding: 12px 14px; text-align: left; border-bottom: 2px solid #e5e7eb; }
  td { padding: 12px 14px; font-size: 13px; color: #4b5563; border-bottom: 1px solid #f3f4f6; }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background: #f9fafb; }
  .total-row { background: #f3f4f6 !important; font-weight: 600; }
  .total-row td { border-top: 2px solid #e5e7eb; padding-top: 14px; padding-bottom: 14px; }
  .right { text-align: right; }
  .center { text-align: center; }
  .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 16px; margin-top: 12px; }
  .summary-item { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center; }
  .summary-label { font-size: 11px; color: #6b7280; font-weight: 500; margin-bottom: 6px; }
  .summary-value { font-size: 18px; font-weight: 700; color: #1f2937; }
  .note-content { white-space: pre-wrap; line-height: 1.6; font-size: 13px; color: #4b5563; }
  @media print {
    body { background: white; }
    .wrap { padding: 20px; }
    .section { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="wrap">${node.innerHTML}</div>
  <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
</body>
</html>`;
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (e) {
      message.error('인쇄에 실패했습니다.');
    }
  };

  const onGeneratePDFAndUpload = async () => {
    try {
      if (!validateInvoiceOptions()) return;
      const node = printRef.current;
      if (!node) {
        message.error('인보이스 내용을 찾을 수 없습니다.');
        return;
      }

      setLoading(true);
      const hide = message.loading('PDF 생성 중...', 0);

      // Generate PDF using html2canvas and jsPDF
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileName = `Invoice_${orderNo || 'unknown'}_${timestamp}.pdf`;

      // Convert PDF to Blob
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      hide();

      // If groupNo or saleNo is available, upload to group info
      if (saleNo) {
        const uploadHide = message.loading('단체정보에 업로드 중...', 0);
        try {
          // Get current group info to preserve existing data
          const groupInfo = await getGroupEventInfo({ saleNo });
          
          if (!groupInfo?.단체번호) {
            throw new Error('단체번호를 찾을 수 없습니다.');
          }

          // Upload to S3
          const filePath = `1/EventGroup/${groupInfo.단체번호}/`;
          const uploadResult = await uploadS3({ 
            file: pdfFile, 
            fileName, 
            filePath 
          });

          const uploadedUrl = uploadResult?.url || '';
          if (!uploadedUrl) {
            throw new Error('업로드 응답에 url이 없습니다.');
          }

          // Derive stored path
          const prefix = 'https://kr.object.iwinv.kr/orgin/';
          const storedPath = uploadedUrl.startsWith(prefix) 
            ? uploadedUrl.slice(prefix.length) 
            : `${filePath}${fileName}`;

          // Save attachment to group info
          await saveGroupInfoAttachment({
            groupNo: groupInfo.단체번호,
            groupName: groupInfo?.행사단체명 || '',
            managerNo: groupInfo?.수배담당번호 || '',
            startDate: groupInfo?.시작일 || '',
            endDate: groupInfo?.종료일 || '',
            attachFileName: fileName,
            attachFilePath: storedPath,
            remark: groupInfo?.단체비고 || '',
            preDepositClose: false,
            autoJoinClose: '',
            landCompanyNo: '',
          });

          uploadHide();
          message.success('PDF가 생성되고 단체정보에 업로드되었습니다.');
        } catch (uploadErr) {
          uploadHide();
          console.error('Upload error:', uploadErr);
          message.warning(`PDF는 생성되었으나 업로드에 실패했습니다: ${uploadErr?.message || '알 수 없는 오류'}`);
          
          // Still download the PDF locally
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
        }
      } else {
        // No saleNo, just download locally
        message.success('PDF가 생성되었습니다.');
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('PDF generation error:', e);
      message.error(`PDF 생성에 실패했습니다: ${e?.message || '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF, upload, and open email modal
  const onGeneratePDFAndSendEmail = async () => {
    try {
      if (!validateInvoiceOptions()) return;
      const node = printRef.current;
      if (!node) {
        message.error('인보이스 내용을 찾을 수 없습니다.');
        return;
      }

      // Check SMTP settings
      if (!smtpSettings) {
        message.error('SMTP 설정이 없습니다. 설정 > 발신메일 관리에서 SMTP를 설정해주세요.');
        return;
      }

      setLoading(true);
      const hide = message.loading('PDF 생성 및 업로드 중...', 0);

      // Generate PDF using html2canvas and jsPDF
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileName = `Invoice_${orderNo || 'unknown'}_${timestamp}.pdf`;

      // Convert PDF to Blob
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      let finalPdfUrl = '';

      // Upload to group info if saleNo is available
      if (saleNo) {
        try {
          const groupInfo = await getGroupEventInfo({ saleNo });
          
          if (!groupInfo?.단체번호) {
            throw new Error('단체번호를 찾을 수 없습니다.');
          }

          // Upload to S3
          const filePath = `1/EventGroup/${groupInfo.단체번호}/`;
          const uploadResult = await uploadS3({ 
            file: pdfFile, 
            fileName, 
            filePath 
          });

          finalPdfUrl = uploadResult?.url || '';
          if (!finalPdfUrl) {
            throw new Error('업로드 응답에 url이 없습니다.');
          }

          // Derive stored path
          const prefix = 'https://kr.object.iwinv.kr/orgin/';
          const storedPath = finalPdfUrl.startsWith(prefix) 
            ? finalPdfUrl.slice(prefix.length) 
            : `${filePath}${fileName}`;

          // Save attachment to group info
          await saveGroupInfoAttachment({
            groupNo: groupInfo.단체번호,
            groupName: groupInfo?.행사단체명 || '',
            managerNo: groupInfo?.수배담당번호 || '',
            startDate: groupInfo?.시작일 || '',
            endDate: groupInfo?.종료일 || '',
            attachFileName: fileName,
            attachFilePath: storedPath,
            remark: groupInfo?.단체비고 || '',
            preDepositClose: false,
            autoJoinClose: '',
            landCompanyNo: '',
          });
        } catch (uploadErr) {
          console.error('Upload error:', uploadErr);
          hide();
          message.error(`PDF 업로드에 실패했습니다: ${uploadErr?.message || '알 수 없는 오류'}`);
          setLoading(false);
          return;
        }
      } else {
        hide();
        message.error('판매번호가 없어 PDF를 업로드할 수 없습니다.');
        setLoading(false);
        return;
      }

      hide();
      setLoading(false);

      // Set uploaded PDF URL and open email modal
      setUploadedPdfUrl(finalPdfUrl);
      
      // Set default email values
      const defaultRecipient = propOrdererEmail || reservationInfo?.주문자이메일 || reservationInfo?.이메일 || '';
      const productName = saleInfo?.상품명 || saleInfo?.판매상품명 || reservationInfo?.판매상품명 || reservationInfo?.상품명 || '';
      
      setEmailRecipient(defaultRecipient);
      setEmailSubject(`[인보이스] ${productName || '여행상품'} - ${orderNo || ''}`);
      setEmailBody(getDefaultEmailBody(finalPdfUrl, company?.업체명, productName, eventPeriod));
      setEmailModalOpen(true);

    } catch (e) {
      console.error('PDF generation error:', e);
      message.error(`PDF 생성에 실패했습니다: ${e?.message || '알 수 없는 오류'}`);
      setLoading(false);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailRecipient?.trim()) {
      message.error('수신자 이메일을 입력해주세요.');
      return;
    }
    if (!emailSubject?.trim()) {
      message.error('메일 제목을 입력해주세요.');
      return;
    }
    if (!smtpSettings) {
      message.error('SMTP 설정이 없습니다.');
      return;
    }

    try {
      setSendingEmail(true);
      const hide = message.loading('이메일 발송 중...', 0);

      // Parse recipients (comma or semicolon separated)
      const recipients = emailRecipient
        .split(/[,;]/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

      if (recipients.length === 0) {
        hide();
        message.error('유효한 수신자 이메일이 없습니다.');
        setSendingEmail(false);
        return;
      }

      await sendEmail({
        recipients,
        subject: emailSubject,
        body: emailBody,
        smtpServer: smtpSettings?.서버주소 || '',
        smtpPort: smtpSettings?.포트 || 465,
        smtpUsername: smtpSettings?.메일주소 || '',
        smtpPassword: smtpSettings?.비밀번호 || '',
      });

      hide();
      message.success('이메일이 성공적으로 발송되었습니다.');
      setEmailModalOpen(false);
    } catch (e) {
      console.error('Email send error:', e);
      message.error(`이메일 발송에 실패했습니다: ${e?.message || '알 수 없는 오류'}`);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      title="인보이스"
      width={920}
      destroyOnClose
      maskClosable={false}
    >
      <div className="space-y-6">
        {/* Input Section */}
        <div className="bg-gray-50 p-6 rounded-2xl">
          <div className="flex items-center mb-4">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-800">인보이스 정보 입력</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">인보이스 구분</label>
              <div className="flex flex-wrap gap-4 items-center">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="invoiceType"
                    checked={invoiceType === 'full'}
                    onChange={() => setInvoiceType('full')}
                  />
                  전체
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="invoiceType"
                    checked={invoiceType === 'deposit'}
                    onChange={() => setInvoiceType('deposit')}
                  />
                  신청금
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="invoiceType"
                    checked={invoiceType === 'balance'}
                    onChange={() => setInvoiceType('balance')}
                  />
                  잔금
                </label>
                {invoiceType === 'deposit' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">비율(%) 또는 금액(원)</span>
                    <input
                      type="text"
                      className="w-28 border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm"
                      value={formatNumberWithCommas(depositPercent)}
                      onChange={(e) => {
                        const v = e.target.value.replace(/,/g, '');
                        if (v === '' || /^\d+$/.test(v)) {
                          setDepositPercent(v === '' ? '' : Number(v));
                        }
                      }}
                      placeholder="예: 30"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showTravelerDetails}
                  onChange={(e) => setShowTravelerDetails(e.target.checked)}
                />
                예약인원 상세 및 인원별 금액 표시
              </label>
            </div>
            <div className="space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">수신자 회사</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm" 
                value={receiverCompany} 
                onChange={(e) => setReceiverCompany(e.target.value)} 
                placeholder="예: ABC 여행사" 
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">수신자 이름</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm" 
                value={receiverName} 
                onChange={(e) => setReceiverName(e.target.value)} 
                placeholder="예: 홍길동" 
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">비고사항</label>
              <textarea 
                className="w-full border border-gray-300 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm h-20" 
                value={note} 
                onChange={(e) => setNote(e.target.value)} 
                placeholder="특이사항, 안내문 등" 
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">취소료 규정</label>
              <textarea 
                className="w-full border border-gray-300 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm h-24" 
                value={cancelPolicy} 
                onChange={(e) => setCancelPolicy(e.target.value)} 
                placeholder="취소료 규정 입력" 
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">입금계좌번호</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm" 
                value={bankAccount} 
                onChange={(e) => setBankAccount(e.target.value)} 
                placeholder="예: 국민 123-456-789012 예금주 (주)회사명" 
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-100">
          <div className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            미리보기
          </div>
          <div className="flex gap-2">
            <AntButton onClick={onClose} size="large">닫기</AntButton>
            <AntButton onClick={onPrint} loading={loading} size="large" icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            }>인쇄</AntButton>
            <AntButton type="primary" onClick={onGeneratePDFAndUpload} loading={loading} size="large" icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            }>PDF 생성 및 업로드</AntButton>
            <AntButton 
              type="primary" 
              onClick={onGeneratePDFAndSendEmail} 
              loading={loading} 
              size="large" 
              style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            >이메일 발송</AntButton>
          </div>
        </div>

        {/* Invoice Preview */}
        <div ref={printRef} className="light-scope bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div style={{ padding: '40px 32px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', paddingBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937', letterSpacing: '-0.5px', marginBottom: '8px' }}>INVOICE</div>
                <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>주문번호: {orderNo || reservationInfo?.주문번호 || '-'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{company?.업체명 || ''}</div>
                {company?.회사로고 ? (
                  <img src={company.회사로고} alt="logo" style={{ width: 100, height: 40, objectFit: 'contain' }} />
                ) : null}
              </div>
            </div>

            {/* Sender & Receiver */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, marginBottom: '4px' }}>수신자</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', lineHeight: 1.5 }}>{receiverCompany ? `${receiverCompany} / ` : ''}{receiverName || '-'}</div>
              </div>
              <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, marginBottom: '4px' }}>발신자</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', lineHeight: 1.5 }}>{company?.업체명 || '-'}</div>
              </div>
            </div>

            {/* Product/Reservation Info */}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>상품 및 예약 정보</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '8px', overflow: 'hidden' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>상품명</th>
                    <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>행사기간</th>
                    <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>예약자</th>
                    <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>인원</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>{saleInfo?.상품명 || saleInfo?.판매상품명 || reservationInfo?.판매상품명 || reservationInfo?.상품명 || '-'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', textAlign: 'center' }}>{eventPeriod || '-'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', textAlign: 'center' }}>{reservationInfo?.주문자 || saleInfo?.주문자 || saleInfo?.예약고객명 || '-'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', textAlign: 'center' }}>{travelerSummary.total || (Number(reservationInfo?.총인원) || Number(saleInfo?.총인원) || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Traveler Details */}
            {showTravelerDetails && travelerRows.length > 0 && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>예약인원 상세 및 인원별 금액</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr>
                      <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', width: 60 }}>No</th>
                      <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>이름</th>
                      <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>연령구분</th>
                      <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>상품가</th>
                      <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>추가판매가</th>
                      <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {travelerRows.map(r => (
                      <tr key={r.idx}>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>{r.idx}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', borderBottom: '1px solid #f3f4f6' }}>{r.name}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>{r.group}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{r.price.toLocaleString('ko-KR')}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{r.tax.toLocaleString('ko-KR')}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{r.total.toLocaleString('ko-KR')}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f3f4f6' }}>
                      <td colSpan={3} style={{ padding: '14px', fontSize: '13px', fontWeight: 700, borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>소계</td>
                      <td style={{ padding: '14px', fontSize: '13px', fontWeight: 700, borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>{travelerTotals.price.toLocaleString('ko-KR')}</td>
                      <td style={{ padding: '14px', fontSize: '13px', fontWeight: 700, borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>{travelerTotals.tax.toLocaleString('ko-KR')}</td>
                      <td style={{ padding: '14px', fontSize: '13px', fontWeight: 700, color: '#1f2937', borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>{travelerTotals.total.toLocaleString('ko-KR')}</td>
                    </tr>
                    {Number(saleBreakdown.대리점커미션) > 0 && (
                      <tr style={{ background: '#f9fafb' }}>
                        <td colSpan={5} style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 700, color: '#4b5563', textAlign: 'right' }}>대리점커미션</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 700, color: '#b91c1c', textAlign: 'right' }}>-{Number(saleBreakdown.대리점커미션).toLocaleString('ko-KR')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Sale Breakdown */}
            <div style={{ background: '#eff6ff', border: '2px solid #60a5fa', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 10px 22px rgba(37, 99, 235, 0.12)' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: '#1d4ed8', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>입금 요청 금액</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', borderRadius: '8px', overflow: 'hidden' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>항목</th>
                    <th style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '12px', padding: '12px 14px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>금액(원)</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceType === 'full' ? (
                    <>
                      <tr>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>상품금액</td>
                        <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#1e3a8a', textAlign: 'right' }}>{(Number(saleBreakdown.상품금액) || 0).toLocaleString('ko-KR')}</td>
                      </tr>
                      {Number(saleBreakdown.추가서비스합계) > 0 && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>추가 서비스(옵션)</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#1e3a8a', textAlign: 'right' }}>{(Number(saleBreakdown.추가서비스합계) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      {Number(saleBreakdown.텍스유류) > 0 && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>추가 판매가</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#1e3a8a', textAlign: 'right' }}>{(Number(saleBreakdown.텍스유류) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      {Number(saleBreakdown.프로모션할인) > 0 && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>프로모션할인</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#b91c1c', textAlign: 'right' }}>-{(Number(saleBreakdown.프로모션할인) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      {Number(saleBreakdown.기타할인) > 0 && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>기타할인</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#b91c1c', textAlign: 'right' }}>-{(Number(saleBreakdown.기타할인) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      {Number(saleBreakdown.대리점커미션) > 0 && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>대리점커미션</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#b91c1c', textAlign: 'right' }}>-{(Number(saleBreakdown.대리점커미션) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      {Number(saleBreakdown.취소료) > 0 && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>취소료</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#b91c1c', textAlign: 'right' }}>-{(Number(saleBreakdown.취소료) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      <tr style={{ background: '#f3f4f6' }}>
                        <td style={{ padding: '14px', fontSize: '13px', fontWeight: 700, borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>총 판매가(넷가)</td>
                        <td style={{ padding: '14px', fontSize: '16px', fontWeight: 800, color: '#1d4ed8', borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>{(Number(effectiveSaleTotal) || 0).toLocaleString('ko-KR')}</td>
                      </tr>
                    </>
                  ) : (
                    <>
                      {invoiceType === 'balance' && (
                        <tr>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>선급금</td>
                          <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#1e3a8a', textAlign: 'right' }}>{(Number(prePaidAmount) || 0).toLocaleString('ko-KR')}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#4b5563' }}>{invoiceType === 'deposit' ? '신청금' : '잔금'}</td>
                        <td style={{ padding: '12px 14px', fontSize: '15px', fontWeight: 800, color: '#1d4ed8', textAlign: 'right' }}>{(Number(invoiceRequestAmount) || 0).toLocaleString('ko-KR')}</td>
                      </tr>
                      <tr style={{ background: '#f3f4f6' }}>
                        <td style={{ padding: '14px', fontSize: '13px', fontWeight: 700, borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>{invoiceType === 'deposit' ? '신청금 합계' : '잔금 합계'}</td>
                        <td style={{ padding: '14px', fontSize: '16px', fontWeight: 900, color: '#1d4ed8', borderTop: '2px solid #e5e7eb', textAlign: 'right' }}>{(Number(invoiceRequestAmount) || 0).toLocaleString('ko-KR')}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            {!!note && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>비고사항</div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '13px', color: '#4b5563' }}>{note}</div>
              </div>
            )}

            {/* Cancel Policy */}
            {!!cancelPolicy && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>취소료 규정</div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '13px', color: '#4b5563' }}>{cancelPolicy}</div>
              </div>
            )}

            {/* Bank Account */}
            {!!bankAccount && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>입금계좌</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', lineHeight: 1.5 }}>{bankAccount}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Send Modal */}
      <AntModal
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        footer={null}
        title={
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            인보이스 이메일 발송
          </div>
        }
        width={640}
        destroyOnClose
        maskClosable={false}
      >
        <div className="space-y-4 pt-4">
          {/* SMTP Status */}
          <div className={`p-3 rounded-lg text-sm ${smtpSettings ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
            {smtpSettings ? (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                SMTP 설정 완료 ({smtpSettings?.서버주소})
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                SMTP 설정이 필요합니다. 설정 &gt; 발신메일 관리에서 설정해주세요.
              </div>
            )}
          </div>

     

          {/* Recipient */}
          <div className="space-y-2">
            <label className="block text-[13px] font-semibold text-gray-600">
              수신자 이메일 <span className="text-red-500">*</span>
            </label>
            <input 
              className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm" 
              value={emailRecipient} 
              onChange={(e) => setEmailRecipient(e.target.value)} 
              placeholder="example@email.com (여러 개는 쉼표로 구분)" 
            />
            <p className="text-xs text-gray-500">여러 수신자는 쉼표(,) 또는 세미콜론(;)으로 구분하세요.</p>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label className="block text-[13px] font-semibold text-gray-600">
              메일 제목 <span className="text-red-500">*</span>
            </label>
            <input 
              className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm" 
              value={emailSubject} 
              onChange={(e) => setEmailSubject(e.target.value)} 
              placeholder="메일 제목을 입력하세요" 
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <label className="block text-[13px] font-semibold text-gray-600">메일 본문</label>
            <AntTabs
              defaultActiveKey="preview"
              items={[
                {
                  key: 'edit',
                  label: '편집',
                  children: (
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm h-48 font-mono"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="메일 본문을 입력하세요 (HTML 태그 사용 가능)"
                    />
                  ),
                },
                {
                  key: 'preview',
                  label: '미리보기',
                  children: (
                    <div
                      className="border border-gray-200 rounded-lg bg-white p-4 max-h-96 overflow-auto"
                      dangerouslySetInnerHTML={{ __html: emailBody || '' }}
                    />
                  ),
                },
              ]}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <AntButton onClick={() => setEmailModalOpen(false)} size="large">
              취소
            </AntButton>
            <AntButton 
              type="primary" 
              onClick={handleSendEmail} 
              loading={sendingEmail}
              disabled={!smtpSettings}
              size="large"
              style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              }
            >
              발송하기
            </AntButton>
          </div>
        </div>
      </AntModal>
    </AntModal>
  );
};

export default InvoiceModal;
